/**
 * VIB-728 A: Horisontal strip øverst i ChatRoomList med online-brukere.
 *
 * Bruker eksisterende usePresence-hook + useUsers for å vise hvem som er
 * online/idle. Klikk på avatar starter eller åpner DM med personen.
 */
import { usePresence, getPresenceStatus } from "@/hooks/use-presence";
import { useUsers } from "@/hooks/use-users";
import { PresenceDot } from "@/components/shared/PresenceDot";

interface Props {
  currentUserId: string;
  onSelectUser: (userId: string) => void;
}

export function ActiveNowStrip({ currentUserId, onSelectUser }: Props) {
  const presenceMap = usePresence(currentUserId);
  const { data: users = [] } = useUsers();

  const activeUsers = users.filter((u) => {
    if (u.id === currentUserId) return false;
    const status = getPresenceStatus(presenceMap, u.id);
    return status === "online" || status === "idle";
  });

  if (activeUsers.length === 0) return null;

  return (
    <div className="px-3 py-2 border-b shrink-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">
        Aktive nå · {activeUsers.length}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {activeUsers.map((u) => {
          const initials = u.full_name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
          const firstName = u.full_name.split(" ")[0];
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => onSelectUser(u.id)}
              className="flex flex-col items-center gap-1 min-w-[48px] shrink-0 hover:bg-muted/40 rounded-md p-1 transition-colors"
              title={u.full_name}
            >
              <div className="relative">
                {u.avatar_url ? (
                  <img
                    src={u.avatar_url}
                    alt={u.full_name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-primary/10 text-primary text-xs inline-flex items-center justify-center font-medium">
                    {initials}
                  </span>
                )}
                <PresenceDot
                  userId={u.id}
                  size="md"
                  className="absolute bottom-0 right-0"
                />
              </div>
              <span className="text-[10px] truncate w-12 text-center text-muted-foreground">
                {firstName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
